/**
 * The referee. Everything that is worth points, with the cards that made it,
 * so the UI can highlight each combination and the coach can name it.
 */

import { type Card, combinations, rankOf, suitOf, valueOf } from './cards.js';

export type ScoreKind = 'fifteen' | 'pair' | 'run' | 'flush' | 'nobs' | 'thirtyone' | 'go' | 'lastcard' | 'heels';

export interface ScoreItem {
  kind: ScoreKind;
  /** The cards that make this combination (ids). */
  cards: Card[];
  points: number;
}

export interface Score {
  total: number;
  items: ScoreItem[];
}

const KIND_LABEL: Record<ScoreKind, string> = {
  fifteen: 'Fifteen',
  pair: 'Pair',
  run: 'Run',
  flush: 'Flush',
  nobs: 'Nobs',
  thirtyone: 'Thirty-one',
  go: 'Go',
  lastcard: 'Last card',
  heels: 'His heels',
};

export function itemLabel(it: ScoreItem): string {
  switch (it.kind) {
    case 'pair':
      return it.cards.length === 2 ? 'Pair' : it.cards.length === 3 ? 'Pair royal' : 'Double pair royal';
    case 'run':
      return `Run of ${it.cards.length}`;
    case 'flush':
      return `Flush of ${it.cards.length}`;
    default:
      return KIND_LABEL[it.kind];
  }
}

/**
 * Score a four-card hand (or crib) with the starter.
 *
 * Fifteens: every subset of the five cards that counts 15, two each.
 * Pairs: every two cards of a rank, two each — so three of a kind is 6, four is 12.
 * Runs: three or more consecutive ranks, one point per card, once per distinct
 *   combination — a double run of three is two runs (6), a double-double is four
 *   (12), a triple run is three (9). Only the longest run counts; a run of four
 *   is not also two runs of three.
 * Flush: four hand cards of one suit, 4; five with the starter, 5. In the crib
 *   only the five-card flush counts (ACC rule 7.6).
 * Nobs: the jack in the hand (not the starter) of the starter's suit, 1.
 */
export function scoreHand(hand: readonly Card[], starter: Card, isCrib: boolean): Score {
  const all = [...hand, starter];
  const items: ScoreItem[] = [];

  // Fifteens
  for (let k = 2; k <= 5; k++) {
    for (const combo of combinations(all, k)) {
      let t = 0;
      for (const c of combo) t += valueOf(c);
      if (t === 15) items.push({ kind: 'fifteen', cards: combo, points: 2 });
    }
  }

  // Pairs
  for (const [a, b] of combinations(all, 2) as [Card, Card][]) {
    if (rankOf(a) === rankOf(b)) items.push({ kind: 'pair', cards: [a, b], points: 2 });
  }

  // Runs
  items.push(...runsIn(all));

  // Flush
  const hs = suitOf(hand[0]!);
  if (hand.every((c) => suitOf(c) === hs)) {
    if (suitOf(starter) === hs) items.push({ kind: 'flush', cards: all, points: 5 });
    else if (!isCrib) items.push({ kind: 'flush', cards: [...hand], points: 4 });
  }

  // Nobs
  for (const c of hand) {
    if (rankOf(c) === 11 && suitOf(c) === suitOf(starter)) items.push({ kind: 'nobs', cards: [c, starter], points: 1 });
  }

  return { total: items.reduce((s, i) => s + i.points, 0), items };
}

/** Every maximal run (length >= 3) in a set of cards, expanded to card combos. */
function runsIn(cards: readonly Card[]): ScoreItem[] {
  const byRank = new Map<number, Card[]>();
  for (const c of cards) {
    const r = rankOf(c);
    const arr = byRank.get(r);
    if (arr) arr.push(c);
    else byRank.set(r, [c]);
  }
  const ranks = [...byRank.keys()].sort((a, b) => a - b);
  const out: ScoreItem[] = [];
  let i = 0;
  while (i < ranks.length) {
    let j = i;
    while (j + 1 < ranks.length && ranks[j + 1] === ranks[j]! + 1) j++;
    const len = j - i + 1;
    if (len >= 3) {
      // Cartesian product of the card groups for each rank in the run.
      let combos: Card[][] = [[]];
      for (let r = i; r <= j; r++) {
        const group = byRank.get(ranks[r]!)!;
        const next: Card[][] = [];
        for (const partial of combos) for (const c of group) next.push([...partial, c]);
        combos = next;
      }
      for (const combo of combos) out.push({ kind: 'run', cards: combo, points: len });
    }
    i = j + 1;
  }
  return out;
}

/**
 * Score the LAST card played onto the current sub-count. `stack` is every card
 * played since the count last reset, in order, the new card last.
 *
 * Fifteen 2; thirty-one 2 (the engine adds no extra last-card point for 31).
 * Pairs: the new card against the immediately preceding cards of the same
 * rank: 2, 6, 12. Runs: the longest tail of the stack whose ranks form a
 * consecutive set in any order (3..7 cards), one point per card.
 */
export function scorePeg(stack: readonly Card[]): Score {
  const items: ScoreItem[] = [];
  if (stack.length === 0) return { total: 0, items };
  let count = 0;
  for (const c of stack) count += valueOf(c);
  const last = stack[stack.length - 1]!;

  if (count === 15) items.push({ kind: 'fifteen', cards: [...stack], points: 2 });
  if (count === 31) items.push({ kind: 'thirtyone', cards: [...stack], points: 2 });

  // Pairs: trailing same rank
  let n = 1;
  while (n < stack.length && rankOf(stack[stack.length - 1 - n]!) === rankOf(last)) n++;
  if (n >= 2) {
    const cards = stack.slice(stack.length - n);
    items.push({ kind: 'pair', cards, points: n === 2 ? 2 : n === 3 ? 6 : 12 });
  }

  // Runs: longest tail that is a permutation of consecutive ranks
  for (let len = Math.min(stack.length, 7); len >= 3; len--) {
    const tail = stack.slice(stack.length - len);
    const ranks = tail.map(rankOf).sort((a, b) => a - b);
    let ok = true;
    for (let k = 1; k < ranks.length; k++) {
      if (ranks[k] !== ranks[k - 1]! + 1) {
        ok = false;
        break;
      }
    }
    if (ok) {
      items.push({ kind: 'run', cards: tail, points: len });
      break;
    }
  }

  return { total: items.reduce((s, i) => s + i.points, 0), items };
}

/** The count of a pegging stack. */
export function pegCount(stack: readonly Card[]): number {
  let t = 0;
  for (const c of stack) t += valueOf(c);
  return t;
}

/** Plain-English summary of a score, e.g. "fifteen 2, fifteen 4, and a pair is 6". */
export function describeScore(score: Score): string {
  if (score.total === 0) return 'nothing';
  const parts: string[] = [];
  let running = 0;
  const fifteens = score.items.filter((i) => i.kind === 'fifteen');
  for (const _ of fifteens) {
    running += 2;
    parts.push(`fifteen ${running}`);
  }
  for (const it of score.items) {
    if (it.kind === 'fifteen') continue;
    running += it.points;
    parts.push(`${itemLabel(it).toLowerCase()} ${running}`);
  }
  return parts.join(', ');
}
